import React, { useState, useEffect, useRef } from "react";
import "./TheArena.scss";

/**
 * THE ARENA - DevourPlay's Gaming Command Center
 * Where quests, challenges, and guilds come alive
 */
const TheArena: React.FC = () => {
    const [arenaEnergy, setArenaEnergy] = useState(0);
    const [activeMode, setActiveMode] = useState<"quests" | "challenges" | "guild" | "rewards">("quests");
    const [selectedQuest, setSelectedQuest] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Arena initialization sequence
    useEffect(() => {
        const initSequence = async () => {
            // Energy buildup animation
            for (let i = 0; i <= 100; i += 2) {
                setArenaEnergy(i);
                await new Promise(resolve => setTimeout(resolve, 20));
            }

            setTimeout(() => setIsLoading(false), 1000);
        };

        initSequence();
    }, []);

    // Particle effect for atmosphere
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const particles: Array<{
            x: number;
            y: number;
            vx: number;
            vy: number;
            size: number;
            color: string;
            life: number;
        }> = [];

        const colors = ["#FF4238", "#7859E6", "#F7C84C"];

        const createParticle = () => {
            const side = Math.floor(Math.random() * 4);
            let x; let y; let vx; let vy;

            switch (side) {
                case 0: // top
                    x = Math.random() * canvas.width;
                    y = 0;
                    vx = (Math.random() - 0.5) * 2;
                    vy = Math.random() * 2;
                    break;
                case 1: // right
                    x = canvas.width;
                    y = Math.random() * canvas.height;
                    vx = -Math.random() * 2;
                    vy = (Math.random() - 0.5) * 2;
                    break;
                case 2: // bottom
                    x = Math.random() * canvas.width;
                    y = canvas.height;
                    vx = (Math.random() - 0.5) * 2;
                    vy = -Math.random() * 2;
                    break;
                default: // left
                    x = 0;
                    y = Math.random() * canvas.height;
                    vx = Math.random() * 2;
                    vy = (Math.random() - 0.5) * 2;
            }

            particles.push({
                x, y, vx, vy,
                size: Math.random() * 3 + 1,
                color: colors[Math.floor(Math.random() * colors.length)],
                life: 1,
            });
        };

        const animate = () => {
            ctx.fillStyle = "rgba(11, 11, 11, 0.1)";
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            if (Math.random() < 0.1) createParticle();

            particles.forEach((particle, index) => {
                particle.x += particle.vx;
                particle.y += particle.vy;
                particle.life -= 0.01;

                ctx.beginPath();
                ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
                ctx.fillStyle = particle.color;
                ctx.globalAlpha = particle.life;
                ctx.fill();
                ctx.globalAlpha = 1;

                if (particle.life <= 0 ||
            particle.x < -10 || particle.x > canvas.width + 10 ||
            particle.y < -10 || particle.y > canvas.height + 10) {
                    particles.splice(index, 1);
                }
            });

            requestAnimationFrame(animate);
        };

        animate();
    }, []);

    const questCategories = [
        {
            id: "daily",
            name: "DAILY GRIND",
            icon: "☀️",
            color: "#F7C84C",
            quests: [
                { id: "daily-1", title: "First Blood", game: "Valorant", xp: 100, progress: 1, total: 3 },
                { id: "daily-2", title: "Squad Goals", game: "Fortnite", xp: 150, progress: 2, total: 5 },
                { id: "daily-3", title: "Loot Goblin", game: "Apex", xp: 200, progress: 0, total: 10 },
            ],
        },
        {
            id: "brand",
            name: "BRAND QUESTS",
            icon: "🏆",
            color: "#FF4238",
            quests: [
                {
                    id: "snapple-1",
                    title: "Snapple Refresh Challenge",
                    brand: "Snapple",
                    reward: "Exclusive Skin + 1000 Fuel",
                    timeLeft: "2D 14H",
                    featured: true,
                },
                {
                    id: "monster-1",
                    title: "Monster Clutch Tournament",
                    brand: "Monster Energy",
                    reward: "$10,000 Prize Pool",
                    timeLeft: "5H 32M",
                    featured: true,
                },
            ],
        },
        {
            id: "guild",
            name: "GUILD OPS",
            icon: "⚔️",
            color: "#7859E6",
            quests: [
                { id: "guild-1", title: "Fortress Defense", collective: true, participants: 12, required: 15 },
                { id: "guild-2", title: "Weekly Domination", collective: true, progress: 65, total: 100 },
            ],
        },
    ];

    if (isLoading) {
        return (
            <div className="arena-loading">
                <canvas ref={canvasRef} className="particle-canvas" />
                <div className="loading-content">
                    <div className="arena-logo">
                        <div className="logo-hexagon">
                            <div className="hexagon-inner" />
                            <div className="hexagon-pulse" />
                            <span className="logo-text">ARENA</span>
                        </div>
                    </div>

                    <div className="loading-bar">
                        <div className="bar-track">
                            <div className="bar-fill" style={{ width: `${arenaEnergy}%` }} />
                            <div className="bar-glow" style={{ width: `${arenaEnergy}%` }} />
                        </div>
                        <div className="loading-text">
                            {arenaEnergy < 30 && "INITIALIZING ARENA SYSTEMS..."}
                            {arenaEnergy >= 30 && arenaEnergy < 60 && "LOADING QUEST DATABASE..."}
                            {arenaEnergy >= 60 && arenaEnergy < 90 && "SYNCING GUILD STATUS..."}
                            {arenaEnergy >= 90 && "READY TO DOMINATE"}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="the-arena">
            <canvas ref={canvasRef} className="particle-canvas" />

            {/* ARENA HUD */}
            <div className="arena-hud">
                {/* TOP BAR - Player Status */}
                <div className="status-bar">
                    <div className="player-status">
                        <div className="player-avatar">
                            <img src="https://via.placeholder.com/60" alt="Player" />
                            <div className="level-badge">42</div>
                            <div className="status-ring" />
                        </div>
                        <div className="player-info">
                            <h3 className="player-name">DEATHKILLER99</h3>
                            <div className="player-stats">
                                <span className="stat-item">
                                    <span className="stat-icon">⚡</span>
                                    <span className="stat-value">2,847</span>
                                </span>
                                <span className="stat-item premium">
                                    <span className="stat-icon">💎</span>
                                    <span className="stat-value">12</span>
                                </span>
                                <span className="stat-item">
                                    <span className="stat-icon">🔥</span>
                                    <span className="stat-value">7 STREAK</span>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="guild-status">
                        <div className="guild-emblem">
                            <div className="emblem-shape">
                                <span className="emblem-icon">⚔️</span>
                            </div>
                        </div>
                        <div className="guild-info">
                            <div className="guild-name">APEX PREDATORS</div>
                            <div className="guild-rank">#42 GLOBAL</div>
                        </div>
                    </div>

                    <div className="live-alerts">
                        <div className="alert-item urgent">
                            <span className="alert-icon">⚠️</span>
                            <span className="alert-text">Guild War in 2H</span>
                            <span className="alert-timer">PREP NOW</span>
                        </div>
                    </div>
                </div>

                {/* ARENA MODE SELECTOR */}
                <div className="mode-selector">
                    <button
                        className={`mode-btn ${activeMode === "quests" ? "active" : ""}`}
                        onClick={() => setActiveMode("quests")}
                    >
                        <div className="btn-bg" />
                        <span className="btn-icon">🎯</span>
                        <span className="btn-label">QUESTS</span>
                        <span className="btn-count">12</span>
                    </button>
                    <button
                        className={`mode-btn ${activeMode === "challenges" ? "active" : ""}`}
                        onClick={() => setActiveMode("challenges")}
                    >
                        <div className="btn-bg" />
                        <span className="btn-icon">🏆</span>
                        <span className="btn-label">CHALLENGES</span>
                        <span className="btn-count">5</span>
                    </button>
                    <button
                        className={`mode-btn ${activeMode === "guild" ? "active" : ""}`}
                        onClick={() => setActiveMode("guild")}
                    >
                        <div className="btn-bg" />
                        <span className="btn-icon">⚔️</span>
                        <span className="btn-label">GUILD</span>
                        <div className="btn-alert" />
                    </button>
                    <button
                        className={`mode-btn ${activeMode === "rewards" ? "active" : ""}`}
                        onClick={() => setActiveMode("rewards")}
                    >
                        <div className="btn-bg" />
                        <span className="btn-icon">🎁</span>
                        <span className="btn-label">REWARDS</span>
                        <span className="btn-count">3</span>
                    </button>
                </div>

                {/* MAIN ARENA VIEW */}
                <div className="arena-viewport">
                    {activeMode === "quests" &&
            <div className="quest-arena">
                {/* Featured Quest Spotlight */}
                <div className="featured-spotlight">
                    <div className="spotlight-bg">
                        <div className="bg-gradient" />
                        <div className="scan-line" />
                    </div>

                    <div className="spotlight-content">
                        <div className="brand-presenter">
                            <img src="https://upload.wikimedia.org/wikipedia/commons/3/36/Snapple_logo_2020.svg" alt="Snapple" className="brand-logo" />
                            <span className="presents-text">PRESENTS</span>
                        </div>

                        <h1 className="quest-mega-title">FANTASTIC REFRESH CHALLENGE</h1>

                        <div className="quest-details">
                            <div className="detail-item">
                                <span className="detail-icon">🏆</span>
                                <span className="detail-text">Exclusive Fantastic 4 Skin Collection</span>
                            </div>
                            <div className="detail-item">
                                <span className="detail-icon">⏱️</span>
                                <span className="detail-text countdown">2D 14H 32M</span>
                            </div>
                            <div className="detail-item">
                                <span className="detail-icon">👥</span>
                                <span className="detail-text">12,847 COMPETING</span>
                            </div>
                        </div>

                        <button className="engage-btn">
                            <div className="btn-energy" />
                            <span className="btn-text">ENTER CHALLENGE</span>
                            <span className="btn-arrow">→</span>
                        </button>
                    </div>
                </div>

                {/* Quest Categories */}
                <div className="quest-grid">
                    {questCategories.map(category =>
                        <div key={category.id} className="quest-category">
                            <div className="category-header" style={{ "--category-color": category.color } as any}>
                                <span className="category-icon">{category.icon}</span>
                                <h3 className="category-name">{category.name}</h3>
                                <span className="category-count">{category.quests.length}</span>
                            </div>

                            <div className="quest-list">
                                {category.quests.map(quest =>
                                    <div
                                        key={quest.id}
                                        className={`quest-item ${quest.featured ? "featured" : ""} ${selectedQuest === quest.id ? "selected" : ""}`}
                                        onClick={() => setSelectedQuest(quest.id)}
                                    >
                                        <div className="quest-content">
                                            <h4 className="quest-title">{quest.title}</h4>
                                            {quest.game && <span className="quest-game">{quest.game}</span>}
                                            {quest.brand && <span className="quest-brand">{quest.brand}</span>}

                                            {quest.progress !== undefined &&
                              <div className="quest-progress">
                                  <div className="progress-bar">
                                      <div
                                          className="progress-fill"
                                          style={{ width: `${quest.progress / quest.total * 100}%` }}
                                      />
                                  </div>
                                  <span className="progress-text">{quest.progress}/{quest.total}</span>
                              </div>
                                            }

                                            {quest.collective &&
                              <div className="collective-status">
                                  <span className="collective-icon">👥</span>
                                  <span className="collective-text">
                                      {quest.participants}/{quest.required} Squad Members
                                  </span>
                              </div>
                                            }
                                        </div>

                                        <div className="quest-rewards">
                                            {quest.xp && <span className="reward-xp">+{quest.xp} XP</span>}
                                            {quest.reward && <span className="reward-special">{quest.reward}</span>}
                                            {quest.timeLeft && <span className="time-left">{quest.timeLeft}</span>}
                                        </div>
                                    </div>)}
                            </div>
                        </div>)}
                </div>
            </div>
                    }

                    {activeMode === "challenges" &&
            <div className="challenges-arena">
                <h2 className="arena-title">ACTIVE CHALLENGES</h2>
                {/* Challenge tournament brackets, leaderboards, etc */}
            </div>
                    }

                    {activeMode === "guild" &&
            <div className="guild-arena">
                <h2 className="arena-title">GUILD COMMAND</h2>
                {/* Guild member list, operations, wars */}
            </div>
                    }

                    {activeMode === "rewards" &&
            <div className="rewards-arena">
                <h2 className="arena-title">CLAIM YOUR LOOT</h2>
                {/* Unclaimed rewards, lootboxes, etc */}
            </div>
                    }
                </div>

                {/* BOTTOM ACTION BAR */}
                <div className="action-bar">
                    <div className="quick-stats">
                        <div className="stat-block">
                            <span className="stat-label">TODAY'S XP</span>
                            <span className="stat-value">1,234</span>
                            <div className="stat-bar">
                                <div className="bar-fill" style={{ width: "67%" }} />
                            </div>
                        </div>
                        <div className="stat-block">
                            <span className="stat-label">DAILY GOAL</span>
                            <span className="stat-value">3/5</span>
                            <div className="stat-bar">
                                <div className="bar-fill" style={{ width: "60%" }} />
                            </div>
                        </div>
                    </div>

                    <button className="primary-action">
                        <div className="action-bg" />
                        <span className="action-icon">🚀</span>
                        <span className="action-text">QUICK PLAY</span>
                    </button>

                    <div className="social-ticker">
                        <div className="ticker-content">
                            <div className="ticker-item">
                                <span className="ticker-icon">🏆</span>
                                <span className="ticker-text"><strong>Phoenix</strong> just won Monster Tournament!</span>
                            </div>
                            <div className="ticker-item">
                                <span className="ticker-icon">💎</span>
                                <span className="ticker-text"><strong>Your Guild</strong> ranked up to #41!</span>
                            </div>
                            <div className="ticker-item">
                                <span className="ticker-icon">🎁</span>
                                <span className="ticker-text"><strong>3 rewards</strong> waiting to be claimed</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* FLOATING ASSISTANT */}
            <div className="arena-assistant">
                <div className="assistant-avatar">
                    <div className="avatar-ring" />
                    <div className="avatar-core">
                        <span className="avatar-icon">🎮</span>
                    </div>
                </div>
                <div className="assistant-message">
                    <p>Yo! Your guild needs 3 more players for the raid. Want me to send invites?</p>
                    <div className="message-actions">
                        <button className="action-yes">Let's Go</button>
                        <button className="action-no">Not Now</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TheArena;